<template>
    <v-card class="pl-3 border border-transparent" outlined>
        <div v-if="showSkeleton">
            <product-card-skeleton></product-card-skeleton>
        </div>
        <div v-if="!showSkeleton">
            <h1 class="title font-weight-bold px-12 px-sm-4 py-3 pb-0 mobile-position-center text-center text-sm-left text-capitalize">
                {{ productName }}
            </h1>
            <v-card-text class="d-flex mobile-position-center grey--text text-lighten-1 caption pb-0">
                <div class="mr-2" v-if="productArticle">{{$t('articleNumber')}}</div>
                <div>{{ productArticle }}</div>
            </v-card-text>
            <v-card-text class="d-flex product-card-discount-wrapper py-1" v-if="productDiscountPercent > 0">
                <v-chip
                    class="main--yellow"
                    text-color="white"
                    small
                >{{ $t('discount') }}&nbsp;{{ productDiscountPercent.toFixed(2) }}&percnt;</v-chip>
            </v-card-text>
            <v-container class="pl-4 py-0">
                <div class="product-card-actions">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                small
                                fab
                                text
                                icon
                                class="icon-action-btn"
                                @click="authActionHandle({type: 'favorites'})"
                            >
                                <v-icon color="orange lighten-2">{{favoritesIcon}}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('favorites') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                small
                                fab
                                text
                                icon
                                class="icon-action-btn"
                                @click="addToCompare(productData.id)"
                            >
                                <v-img
                                        contain
                                        :src="comparedIcon(productData.id)"
                                        :width="comparedIconSizes.width"
                                        :height="comparedIconSizes.height"
                                ></v-img>
                            </v-btn>
                        </template>
                        <span>{{ $t('compare') }}</span>
                    </v-tooltip>
                </div>

                <v-row>
                    <v-col sm="5" md="5" lg="5">
                        <v-carousel
                                height="300"
                                contain
                                :cycle="false"
                                show-arrows-on-hover
                                hide-delimiter-background
                        >
                            <v-carousel-item
                                    v-for="(image,i) in productImages"
                                    :key="i"
                                    :src="image"
                                    reverse-transition="fade-transition"
                                    transition="fade-transition"
                                    contain
                            ></v-carousel-item>
                        </v-carousel>
                    </v-col>
                    <v-col cols="12" sm="6">

                        <template v-if="isPriceSpecified">
                            <div class="mr-1 text-decoration-line-through" v-if="(productDiscountPercent > 0)">
                                <span>{{ productPriceUnoWithoutDiscount }}</span>
                                <span>{{ productPriceCurrency }}&nbsp;</span>
                                <span>/&nbsp;{{ productPackUnit }}</span>
                            </div>
                            <div class="d-flex flex-wrap align-center">
                                <div class="headline font-weight-bold d-flex align-center flex-wrap">
                                    <span class="mr-2">{{ productPriceUno }}</span>
                                    <span class="mr-1">{{ productPriceCurrency }}</span>
                                    <span class="mr-4"> / 1 {{ productPackUnit }}</span>
                                    <currency-tooltips :prices="productData.currencyPrices" :pack="productPack.value"></currency-tooltips>
                                    <div class="subtitle-1 font-italic font-weight-bold ml-4"> {{ productNds }}</div>
                                </div>
                            </div>
                            <v-card-text v-if="productPack" class="d-flex pa-0">
                                <span class="mr-2">{{ totalPrice }}</span>
                                <span class="mr-2">{{ productPriceCurrency }}</span>
                                <span class="mr-2">{{ $t('for') }}</span>
                                <span>{{ productPack.packName.toLowerCase() }} {{ `${productPack.value} ${productPack.unitName}` }}</span>
                            </v-card-text>
                        </template>
                        <div v-else class="headline">
                            {{ $t('priceNotSpecified') }}
                        </div>

                        <v-card-text class="pa-0 pt-2 grey--text text-body-1 font-weight-bold">
                            <policies-dialog
                                :seller-id="productData.seller.id"
                                :seller-name="sellerTitle"
                                :product-policies="productData.policies"
                            ></policies-dialog>
                        </v-card-text>
                        <div class="d-flex">
                            <v-card-text :class="['pa-2 pl-0 pb-0', existenceColor]">{{ existenceLable }}</v-card-text>
                        </div>
                        <div class="d-flex align-center">
                            <v-card-actions class="pa-0 pr-md-5">
                                <v-rating
                                    :value="productRate.value"
                                    color="orange lighten-2"
                                    dense
                                    small
                                    background-color="orange lighten-2"
                                    class="pa-0"
                                    @input="ratingChange($event, productData.id)"
                                    :readonly="!isUserLoggedIn"
                                ></v-rating>
                                <span class="orange--text text--lighten-2" v-if="productRate.total">( {{ productRate.total }} )</span>
                            </v-card-actions>
                            <v-btn
                                    class="body-2 text--black text-underline-hover text-lowercase"
                                    text
                                    @click="authActionHandle({type: 'feedback'})"
                            >
                                {{$t('addFeedback')}}
                            </v-btn>
                        </div>

                        <div>
                            <v-card-title class="pa-4 pl-0 pb-1 font-weight-bold subtitle-2" v-if="manufacturer">
                                {{$t('producer')}}:
                                <span class="ml-3">
                                    <router-link
                                        class="black--text text-underline-hover"
                                        :to="'/products/attribute/proizvoditel/' + manufacturer.system_name"
                                    >
                                        {{ manufacturer.value.toUpperCase() }}
                                    </router-link>
                                </span>
                            </v-card-title>
                            <v-divider></v-divider>
                            <div class="py-3">
                                <div
                                        class="body-2 py-2"
                                        v-if="productPack"
                                >
                                    <span class="font-weight-bold pr-3">{{ $t('packing') }}:</span>
                                    <span>{{ productPack.packName.toLowerCase()}} {{ `${productPack.value}  ${productPack.unitName}` }}</span>
                                </div>
                                <div
                                        class="body-2"
                                        v-if="productPack && productPack.minValue"
                                >
                                    <span class="font-weight-bold pr-3">{{ $t('minOrder') }}:</span>
                                    <span>{{ productPack.minValue }} {{ productPack.packName.toLowerCase()}}</span>
                                    <v-divider></v-divider>
                                </div>
                            </div>
                            <add-to-basket-dialog
                                :product="productData"
                                :is-open="openAddToBasketDialog"
                                @close="openAddToBasketDialog = false"
                            ></add-to-basket-dialog>

                            <div class="d-flex justify-center pt-6">
                                <v-btn
                                    v-if="!isPriceSpecified"
                                    dark
                                    depressed
                                    class="px-3 subtitle-2 test-white"
                                    color="main--yellow"
                                    justify="center"
                                    @click="authActionHandle({ type: 'message' })"
                                >{{$t('knowPrice')}}</v-btn>

                                <v-btn
                                    id="basket-btn"
                                    v-else-if="!inBasket"
                                    color="main--yellow"
                                    class="px-3 subtitle-2 test-white"
                                    dark
                                    depressed
                                    justify="center"
                                    :disabled="!productData.existence"
                                    @click="openAddDialog"
                                >
                                    <span>{{ $t('buy') }}</span>
                                </v-btn>

                                <v-btn
                                    v-else
                                    dark
                                    depressed
                                    class="px-3 subtitle-2 test-white"
                                    color="main--yellow"
                                    justify="center"
                                    to="/basket"
                                >{{$t('myBasket')}}</v-btn>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </div>
    </v-card>
</template>

<script>

    import './product-card.scss'
    import ActionAccess from '../../../app-front/mixins/ActionAccess';
    import AddToBasket from '../../../app-front/mixins/AddToBasket';
    import AddToBasketDialog from "../../../app-front/components/dialogs/AddToBasketDialog";
    import CurrencyTooltips from '../../../app-front/components/currency-tooltips';
    import Favorites from '../../../app-front/mixins/Favorites';
    import ProductCardSkeleton from '../../../app-front/components/skeletons/ProductCardSkeleton';
    import ProductService from '../../../app-front/services/api/ProductService';
    import ProductsCompare from '../../../app-front/mixins/ProductsCompare';
    import NoLoginBasket from "../../../app-front/mixins/NoLoginBasket";
    import PoliciesDialog from "../basket/v-2/PoliciesDialog";
    import { getPercentNumber } from '../../../utils';

    export default {
        name: "MainInfo",
        props: ['productData', 'productRate'],
        mixins:[ActionAccess, Favorites, AddToBasket, ProductsCompare, NoLoginBasket],
        components: {ProductCardSkeleton, CurrencyTooltips, PoliciesDialog, AddToBasketDialog},
        data() {
          return {
              ndsPrice: '',
              openAddToBasketDialog: false
          }
        },
        computed: {
            inBasket() {
                return this.isBasket(this.productData.id) || this.isInNoLoginBasket(this.productData.id);
            },
            basketName() {
                return this.isBasket(this.productData.id)? this.$t('alreadyInBasket') : this.$t('addToBasket');
            },
            existenceLable() {
                switch (this.productData.existence) {
                    case 0: return this.$t('notAvailable');
                    case 1: return this.$t('inStock');
                    case 2: return this.$t('lowLevel');
                }
            },
            existenceColor() {
                switch (this.productData.existence) {
                    case 0: return 'red--text';
                    case 1: return 'teal--text';
                    case 2: return 'orange--text text--lighten-2';
                }
            },
            favoritesIcon() {
                return this.isFavorite(this.productData.id) ? 'favorite' : 'favorite_border';
            },
            manufacturer() {
                return this.productData.manufacturer || null;
            },
            productAttributes() {
                return this.productData.attributes || [];
            },
            productArticle() {
                return this.productData.articul;
            },
            productImages() {
                return this.productData.images && this.productData.images.length ?
                    this.productData.images : ['/images/default-product.png'];
            },
            productName() {
                return this.productData.productName;
            },
            productNds () {
                return this.productData.price.withNds ? this.$t('withNds') : this.$t('withoutNds');
            },
            productPack() {
                return this.productData.pack;
            },
            productPackUnit() {
                return this.productData.pack ? this.productData.pack.unitName : null;
            },
            totalPrice() {
                return this.productData.price
                    ? getPercentNumber(this.productData.price.total, this.productDiscountPercent).toFixed(2)
                    : this.$t('contractual');
            },
            productPriceUnoWithoutDiscount() {
                const price = this.productData.price && this.productData.price.value ? this.productData.price.value : 0;

                return price.toFixed(2) || this.$t('contractual');
            },
            productDiscountPercent() {
                return this.productData.price.discountPercent ? parseFloat(this.productData.price.discountPercent) : 0;
            },
            productPriceUno() {
                let price = this.productData.price && this.productData.price.value ? this.productData.price.value : 0;
                price = getPercentNumber(price, this.productDiscountPercent);

                return price.toFixed(2) || this.$t('contractual');
            },
            productPriceCurrency() {
                return this.productData.price ? this.productData.price.currency.toUpperCase() : '';
            },
            showSkeleton() {
                return !Object.keys(this.productData).length;
            },
            sellerTitle() {
                if (!this.productData.seller) return '';

                return this.productData.seller.name
            },
            isPriceSpecified() {
                return !!this.productData.price.value;
            }
        },
        methods: {
            authSuccessActionHandle(data) {
                switch(data.type) {
                    case 'favorites' : this.$emit('favorites'); break;
                    case 'order' : this.$emit('order'); break;
                    case 'cart' : this.$emit('cart'); break;
                    case 'feedback' : this.$emit('feedback'); break;
                    case 'message' : this.$emit('message', { type: 'knowPrice' }); break;
                }
            },
            noLoginAction(data) {
                if (data.type == 'cart') {
                    this.addToNoLoginBasket(this.productData.id)
                }
            },
            openAddDialog(){
                this.openAddToBasketDialog = true;
            },
            ratingChange(value, id) {
                const params = {
                    productId: id,
                    value: value,
                };
                ProductService.setProductRate(params)
                    .then(res => {
                        this.$emit('rating-change');
                    })
                    .catch(err => {
                        console.log(err);
                    });
            }
        },

    }
</script>

<style scoped>
    #basket-btn.v-btn--disabled {
        background-color: #ddd!important;
        color: #eee!important;
    }
</style>
